import React, { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import { createRoot } from 'react-dom/client';
import { formatGigTime, getMapParamsFromUrl, updateMapUrlParams, updateVenueUrlParam, updateVenueAndGigUrlParams } from '../fiddly-bits';
import L from 'leaflet'
import 'leaflet.markercluster'

// Custom comedy marker icon
const comedyIcon = L.icon({
  iconUrl: '/images/map-marker-2-32.png',
  iconSize: [32, 32],
  iconAnchor: [18, 32],
  popupAnchor: [0, -36],
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  shadowSize: [42, 42],
  shadowAnchor: [14, 42]
});

const createGigCountIcon = (gigCount, callback) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Set canvas dimensions to match the original marker size
  canvas.width = 32;
  canvas.height = 32;
  
  // Load the original marker image
  const img = new Image();
  img.crossOrigin = 'Anonymous';
  img.onload = function() {
    ctx.scale(1, 1)
    // Draw the original marker image
    ctx.drawImage(img, 0, 0, 32, 32);
    
    // Overlay the gig count number in the center white circle area
    ctx.fillStyle = '#8A2BE2'; // violet color to match marker
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Handle different number lengths with appropriate font sizes
    let fontSize = 13;
    if      (gigCount > 99) fontSize = 9;
    else if (gigCount > 9)  fontSize = 11;
    
    ctx.font = `bold ${fontSize}px Arial`;

    // Draw white outline for better visibility
    ctx.strokeText(gigCount.toString(), 16, 12.5);
    // Draw the number
    ctx.fillText(gigCount.toString(), 16, 12.5);
    
    // Convert canvas to data URL and create icon
    const iconUrl = canvas.toDataURL();
    const icon = L.icon({
      iconUrl: iconUrl,
      iconSize: [32, 32],
      iconAnchor: [18, 32],
      popupAnchor: [0, -36],
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      shadowSize: [42, 42],
      shadowAnchor: [14, 42]
    });
    
    callback(icon);
  };
  
  img.src = '/images/map-marker-2-32.png';
};

// Popup content component
const PopupContent = ({ venue, gigs }) => {
  const scrollContainerRef = useRef(null);

  // Handle gig title click
  const handleGigClick = (gigId, event) => {
    event.preventDefault();
    updateVenueAndGigUrlParams(venue.id, gigId);
  };

  // Scroll to specific gig if gigId is in URL
  useEffect(() => {
    const urlParams = getMapParamsFromUrl();
    if (!urlParams.gigId || !scrollContainerRef.current) return;

    // Small delay to ensure DOM is rendered
    setTimeout(() => {
      const gigElement = scrollContainerRef.current.querySelector(`[data-gig-id="${urlParams.gigId}"]`);
      if (!gigElement) return;

      gigElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Add temporary highlight
      gigElement.classList.add('bg-blue-200');
      setTimeout(() => {
        gigElement.classList.remove('bg-blue-200');
      }, 2000);
    }, 1000);
  }, [venue.id]);

  // Group this popup's Gigs by Act: 
  // [ ..., { acts: [...], gigs: [...]}, ... ]
  const gigsGroupedByActs = [];

  gigs.forEach(gig => {
    const existingActs = gigsGroupedByActs.find(group => {
      const groupActIds = group.acts.map(a => a.id).sort();
      const gigActIds = gig.acts.map(a => a.id).sort();

      return groupActIds.every((id, i) => id == gigActIds[i]);
    });

    if (!existingActs) gigsGroupedByActs.push({ acts: gig.acts, gigs: [gig] });
    else               existingActs.gigs.push(gig);
  });

  return (
    <div className="venue-popup w-[280px] sm:w-[330px] bg-white rounded-md overflow-hidden shadow-lg">
      {venue.primary_image_url && (
        <div className="relative">
          <div className="w-full h-40 overflow-hidden">
            <img 
              src={venue.primary_image_url} 
              alt={venue.name} 
              className="w-full h-full object-cover" 
            />
          </div>

          <div className="absolute bottom-0 p-2 z-10 w-full h-4/5 bg-gradient-to-b from-transparent to-gray-900">
            <div className="absolute bottom-0 p-2">
              <h3 className="text-lg font-bold leading-5 text-gray-100">
                {venue.name}
              </h3>
              <div className="flex items-center mt-0.5">
                <span className="text-gray-200 text-xs truncate">
                  {venue.address || venue.city || ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div ref={scrollContainerRef} id="venue-popup-scroll-container" className="max-h-[320px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">

        <div className="p-1">
          {gigsGroupedByActs.map(group => (

            <div className="mb-3 last:mb-0" key={group.acts.map(act => act.id).join('-')}>
              <div className="mt-1 flex gap-1 w-full h-10 overflow-x-auto no-scrollbar">
                {group.acts.map(act => (
                  <div key={act.id} data-act-id={act.id} title={act.name} className={`inline-flex items-center bg-blue-100 rounded-full p-1 duration-150 transition-all overflow-hidden flex-shrink-0 ${group.acts.length > 1 ? 'w-10 hover:w-28 hover:pr-1' : 'w-auto pr-2'}`}>
                    <div className="w-8 h-8 rounded-full overflow-hidden mr-1 flex-shrink-0">
                      <img 
                        src={act.primary_image_url}
                        alt={act.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-xs text-blue-800 font-medium truncate w-full text-center">
                      {act.name}
                    </span>
                  </div>
                ))}
              </div>

              <div>
                {group.gigs && group.gigs.length > 0 && (
                  <div className="mt-1">
                    {group.gigs.map(gig => (
                      <div key={gig.id} data-gig-id={gig.id} className="p-1 first:pt-0 ml-1 hover:bg-gray-100 transition-all duration-150 rounded">
                        <div className="flex justify-between items-center">
                          <div className="flex-1 min-w-0 flex flex-wrap items-center">
                            <h4 data-role="gig-link" className="truncate">
                              <a 
                                href="#"
                                data-role="gig-name"
                                onClick={(e) => handleGigClick(gig.id, e)}
                                className="font-medium text-xs text-gray-900 hover:text-blue-600 font-semibold transition-colors duration-150 cursor-pointer"
                              >
                                <span data-role="gig-hash" className="invisible"># </span>
                                {gig.name.trim()}
                              </a>
                            </h4>
                            <span className="text-gray-500 text-xs ml-2.5 leading-3">
                              {formatGigTime(gig.start_time, venue)}
                            </span>
                          </div>
                          
                          {gig.ticket_url && (
                            <a 
                              href={gig.ticket_url}
                              data-role="ticket-url"
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="h-full ml-2 shrink-0 px-2.5 py-1 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 transition-colors duration-150"
                            >
                              Tickets
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

        </div>
      </div>
    </div>
  );
};

// Venue markers component
const VenueMarkers = ({ venueGroups }) => {
  const map              = useMap();
  const markersRef       = useRef({});
  const popupStatesRef   = useRef({});
  const markerClusterRef = useRef(null);
  
  const createPopupContent = useCallback((venue, gigs) => {
    const container = document.createElement('div');
    const root = createRoot(container);
    root.render(<PopupContent venue={venue} gigs={gigs} />);
    return container;
  }, []);
  
  useEffect(() => {
    if (!map || markerClusterRef.current) return;

    markerClusterRef.current = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 40,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: true,
      zoomToBoundsOnClick: true
    });
    
    map.addLayer(markerClusterRef.current);

    return () => {
      if (markerClusterRef.current && map) {
        map.removeLayer(markerClusterRef.current);
        markerClusterRef.current = null;
      }
    };
  }, [map]);
  
  useEffect(() => {
    if (!markerClusterRef.current || !map) return;
    
    const currentMarkerIds = new Set();
    
    // Create or update markers for each venue
    venueGroups.forEach(({ venue, gigs }) => {
      const venueId = venue.id;
      currentMarkerIds.add(venueId.toString());
      
      const lat = parseFloat(venue.latitude);
      const lng = parseFloat(venue.longitude);
      
      // Skip invalid coordinates
      if (isNaN(lat) || isNaN(lng)) {
        console.warn(`Invalid coordinates for venue ${venue.name}:`, venue);
        return;
      }
      
      // If marker already exists, update its position and popup content
      if (markersRef.current[venueId]) {
        const marker = markersRef.current[venueId];
        const currentPos = marker.getLatLng();
        
        createGigCountIcon(gigs.length, (newIcon) => {
          marker.setIcon(newIcon);
          if (marker._icon)
            marker._icon.title = `${venue.name} (${gigs.length} gig${gigs.length !== 1 ? 's' : ''})`;
        });
        
        if (currentPos.lat !== lat || currentPos.lng !== lng)
          marker.setLatLng([lat, lng]);
        
        // Always update popup content with new gig data to reflect current filters
        const wasPopupOpen = marker.isPopupOpen();
        
        // Close existing popup first, then unbind it
        if (wasPopupOpen) {
          marker.closePopup();
        }
        marker.unbindPopup();
        
        // Create new popup content with current gig data
        const popupContent = createPopupContent(venue, gigs);
        const popup = L.popup({
          autoClose: false,
          closeOnClick: true,
          className: 'venue-popup-container',
          maxWidth: 340,
          minWidth: 280,
          offset: [0, 10],
          closeButton: true
        }).setContent(popupContent);
        
        marker.bindPopup(popup);
        
        // If popup was open before, reopen it with updated content
        if (wasPopupOpen || popupStatesRef.current[venueId]) {
          setTimeout(() => marker.openPopup(), 100); // Small delay to ensure proper rendering
        }

      } else {
        // Create new marker with gig count icon
        const marker = L.marker([lat, lng], { 
          icon: comedyIcon, // Start with default icon, will be updated asynchronously
          title: `${venue.name} (${gigs.length} gig${gigs.length !== 1 ? 's' : ''})`
        });
        
        // Update marker icon with gig count overlay
        createGigCountIcon(gigs.length, (newIcon) => {
          marker.setIcon(newIcon);
        });
        
        // Create popup with content
        const popupContent = createPopupContent(venue, gigs);
        const popup = L.popup({
          autoClose: false,
          closeOnClick: true,
          className: 'venue-popup-container',
          maxWidth: 340,
          minWidth: 280,
          offset: [0, 10],
          autoPanPadding: [20, 20]
        }).setContent(popupContent);
        
        // Bind popup to marker
        marker.bindPopup(popup);
        
        marker.on('popupopen', () => {
          popupStatesRef.current[venueId] = true;

          const mapCenter = map.getCenter();
          const mapZoom = map.getZoom();
          
          updateMapUrlParams(mapCenter.lat, mapCenter.lng, mapZoom);
          updateVenueUrlParam(venueId);
        });
        
        marker.on('popupclose', () => {
          popupStatesRef.current[venueId] = false;

          const mapCenter = map.getCenter();
          const mapZoom = map.getZoom();
          
          updateMapUrlParams(mapCenter.lat, mapCenter.lng, mapZoom);
          updateVenueAndGigUrlParams(null, null);
        });
        
        // Add to cluster group and store reference
        markerClusterRef.current.addLayer(marker);
        markersRef.current[venueId] = marker;
      }
    });
    
    // Remove stale markers
    Object.keys(markersRef.current).forEach(id => {
      if (!currentMarkerIds.has(id)) {
        markerClusterRef.current.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
        delete popupStatesRef.current[id];
      }
    });
    
    // Check for venue ID in URL and open the corresponding popup after all markers are created
    const { venueId, gigId } = getMapParamsFromUrl();
    if (venueId && markersRef.current[venueId]) {
      setTimeout(() => {
        markersRef.current[venueId].openPopup();
        popupStatesRef.current[venueId] = true;
        
        // If gigId is also present, the PopupContent component will handle scrolling to it
        // via its useEffect hook that watches for gigId in the URL
      }, 300); // Small delay to ensure proper rendering
    }
  }, [map, venueGroups, createPopupContent]);
  
  return null;
};

export default VenueMarkers;
